import $ from 'jquery';
import _ from 'underscore';
import { crossPageSelectHeadRow } from '@bingads-webui/projection-grid-cross-page-select-all';
import { helpers } from '@bingads-webui/projection-grid-toolbar';
import { getGridName } from '@bingads-webui-campaign/inline-download-grids-metadata';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { sortableHeaderTemplate, validateOrder } from '@bingads-webui-campaign/summary-grid-helper';
import { getColumnsConfig } from './columns/index';
import { InlineEditableSave } from './columns/attributes/inline-editable/save';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-groups-tab');
const stateManager = globalContext.require('StateManager/CampaignStateManager');
const preferenceService = globalContext.requireDefault('component/preference/service/index');
const config = globalContext.require('component/config/index');
const decimal = globalContext.require('component/humanize/decimal');

const { toolbarRow } = helpers;
const helpIdMap = {
  AverageCPC: 'Page_CampaignGrids_AvgCPC',
  AveragePosition: 'Page_CampaignGrids_AvgPosition',
  Impressions: 'Page_CampaignGrids_Impr',
  AverageCPM: 'Page_CampaignGrids_AvgCPM',
  PhoneSpend: null,
  PhoneImpressions: 'Page_CampaignGrids_PhoneImpr',
  AverageCPP: 'Page_CampaignGrids_AvgCPP',
  Conversions: 'Page_CampaignGrids_Conv',
  ConversionsCredit: 'Page_CampaignGrids_Conv',
  ConversionRate: 'Page_CampaignGrids_ConvRate',
  PartialConversionRate: 'Page_CampaignGrids_ConvRate',
  AdvertiserReportedRevenue: 'Page_CampaignGrids_Revenue',
  RevenueOnAdSpend: 'Page_CampaignGrids_ReturnAdSpend',
  AuctionWonPercent: 'pop_BA_CampaignGrids_IS',
  AuctionLostToBudgetPercent: 'pop_BA_CampaignGrids_ISBudget',
  AuctionLostToRankPercent: 'pop_BA_CampaignGrids_ISRank',
  AuctionLostToAdQualityPercent: 'pop_BA_CampaignGrids_ISctr',
  AuctionLostToLandingPercent: 'pop_BA_CampaignGrids_ISRelv',
  AuctionLostToBidPercent: 'pop_BA_CampaignGrids_ISBid',
  ClickSharePercent: 'pop_BA_Reporting_ProductAds_ClickSharePercent',
  AbsoluteTopImpressionSharePercent: 'pop_BA_Reporting_ProductAds_AbsoluteTopImpShare',
  ManualCalls: 'pop_BA_Reporting_Clarity_PhoneCalls',
  AllConversions: 'pop_BA_CampaignGrids_AllConv',
  AllConversionCPA: 'pop_BA_CampaignGrids_AllConvCPA',
  AllConversionRate: 'pop_BA_CampaignGrids_AllConvRate',
  AllConversionAdvertiserReportedRevenue: 'pop_BA_CampaignGrids_AllConvRevenue',
  AllConversionRevenueOnAdSpend: 'pop_BA_CampaignGrids_AllConvROAS',
  TotalWatchTimeInMS: 'Page_CampaignGrids_TotalWatchTime',
  ViewThroughConversionsRevenue: 'pop_BA_Reporting_Clarity_ViewThroughConvRevenue',
  ViewThroughConversionsCPA: 'pop_BA_Reporting_Clarity_ViewThroughConvCPA',
  ViewThroughPartialConversionsCPA: 'pop_BA_Reporting_Clarity_ViewThroughConvCPA',
  ViewThroughConversionsReturnOnAdSpend: 'pop_BA_Reporting_Clarity_ViewThroughConvReturnOnAdSpend',
  ViewThroughConversionsRate: 'pop_BA_Reporting_Clarity_ViewThroughConvRate',
  ViewThroughPartialConversionsRate: 'pop_BA_Reporting_Clarity_ViewThroughConvRate',
  ViewThroughConversions: 'pop_BA_CampaignGrids_ViewThrough',
  ViewThroughConversionsCredit: 'pop_BA_CampaignGrids_ViewThrough',
};

export function getConfig({
  dataSource,
  scope,
  view,
  filterService,
  preferenceConfig,
  router,
  isForVNext,
  viewport,
  customHeaderOffset,
  currentCampaign,
  deps,
  globalCampaignTypeFilter,
  isDataFetchManagerSource,
}) {
  const columnHelpId = name => _.result(helpIdMap, name, `Page_CampaignGrids_${name}`);

  const tableName = i18n.getString('EntityType_AdGroup_Plural');
  const permissions = config.get('Permissions');
  const enableGridFlexLayout = _.result(permissions.dynamic, 'GridFlexLayout', false);
  const gridName = getGridName('AdGroup');

  const gridConfig = {
    name: gridName,
    legacyGridName: 'AdGroupGrid',
    tableClasses: enableGridFlexLayout ? ['table'] : ['table', 'table-bordered'],
    tableAttributes: { 'aria-label': tableName },
    dataSource,
    selection: true,
    filterService,
    rows: {
      headRows:
        isForVNext
          ? [
            'column-header-rows',
          ]
          : [
            toolbarRow('lower-toolbar'),
            crossPageSelectHeadRow(),
            'column-header-rows',
          ],
    },
    columns: getColumnsConfig({
      i18n,
      decimal,
      scope,
      view,
      helpId: columnHelpId,
      permissions,
      inlineEditableSave: InlineEditableSave,
      router,
      isForVNext,
      currentCampaign,
      deps,
      globalCampaignTypeFilter,
    }),
    scrolling: {
      header: {
        type: 'sticky',
        offset: customHeaderOffset || (() => $('nav.navbar-default.navbar-fixed-top').height()),
        attributes: {
          'aria-label': i18n.getString('{{tableName}} table header', { tableName }),
          role: 'rowgroup',
        },
      },
      virtualized: true,
    },
    i18n,
    sortableHeader: {
      template: sortableHeaderTemplate,
    },
    stateManager,
    preferenceService,
    config,
    isDataFetchManagerSource,
    ...enableGridFlexLayout && { layout: 'flex' },
  };

  validateOrder(preferenceConfig, gridConfig.columns, gridName, permissions);

  if (viewport) {
    gridConfig.scrolling.viewport = viewport;
  }

  return gridConfig;
}
